import { useSelector } from 'react-redux';

/**
 * Permission Hooks - Phase 5: M6.T6.1
 * 
 * Reusable hooks for permission checking in components
 * 
 * Usage:
 * - useHasPermission('users.view') → boolean
 * - usePermission() → { hasPermission, hasAny, hasAll, getPermissions }
 */

/**
 * Hook to check if user has a specific permission
 * 
 * @param {string} permissionCode - Permission code (e.g., "users.view")
 * @returns {boolean} - true if user has permission
 * 
 * @example
 * const hasViewUsers = useHasPermission('users.view');
 * if (hasViewUsers) {
 *   // Show users page
 * }
 */
export const useHasPermission = (permissionCode) => {
  const { permissions } = useSelector((state) => state.auth);
  
  if (!permissionCode) {
    return false;
  }
  
  // Permission codes are stored in lowercase in DB, so use case-insensitive comparison
  // permissions is an array of strings (not objects), so check directly
  const normalizedCode = permissionCode.toLowerCase();
  return permissions?.some(p => {
    const code = typeof p === 'string' ? p : (p?.code || p);
    return code?.toLowerCase() === normalizedCode;
  }) || false;
};

/**
 * Hook with multiple permission check utilities
 * 
 * @returns {Object} - { hasPermission, hasAny, hasAll, getPermissions }
 * 
 * @example
 * const { hasPermission, hasAny, hasAll } = usePermission();
 * 
 * // Single permission
 * if (hasPermission('users.create')) {
 *   // Show create button
 * }
 * 
 * // OR logic (any permission)
 * if (hasAny(['users.update', 'users.delete'])) {
 *   // Show edit or delete button
 * }
 * 
 * // AND logic (all permissions)
 * if (hasAll(['users.view', 'users.create', 'users.update'])) {
 *   // Show full management UI
 * }
 */
export const usePermission = () => {
  const { permissions } = useSelector((state) => state.auth);
  
  return {
    /**
     * Check single permission
     * @param {string} code - Permission code
     * @returns {boolean}
     */
    hasPermission: (code) => {
      if (!code) {
        return false;
      }
      // Permission codes are stored in lowercase in DB, so use case-insensitive comparison
      const normalizedCode = code.toLowerCase();
      return permissions?.some(p => {
        const code = typeof p === 'string' ? p : (p?.code || p);
        return code?.toLowerCase() === normalizedCode;
      }) || false;
    },
    
    /**
     * Check if user has ANY of the permissions (OR logic)
     * @param {string[]} codes - Array of permission codes
     * @returns {boolean}
     */
    hasAny: (codes) => {
      if (!codes || !Array.isArray(codes) || codes.length === 0) {
        return false;
      }
      // Permission codes are stored in lowercase in DB, so use case-insensitive comparison
      return codes.some(code => {
        const normalizedCode = code.toLowerCase();
        return permissions?.some(p => {
          const permCode = typeof p === 'string' ? p : (p?.code || p);
          return permCode?.toLowerCase() === normalizedCode;
        });
      }) || false;
    },
    
    /**
     * Check if user has ALL of the permissions (AND logic)
     * @param {string[]} codes - Array of permission codes
     * @returns {boolean}
     */
    hasAll: (codes) => {
      if (!codes || !Array.isArray(codes) || codes.length === 0) {
        return false;
      }
      // Permission codes are stored in lowercase in DB, so use case-insensitive comparison
      return codes.every(code => {
        const normalizedCode = code.toLowerCase();
        return permissions?.some(p => {
          const permCode = typeof p === 'string' ? p : (p?.code || p);
          return permCode?.toLowerCase() === normalizedCode;
        });
      }) || false;
    },
    
    /**
     * Get all permissions
     * @returns {string[]}
     */
    getPermissions: () => {
      return permissions || [];
    }
  };
};

export default usePermission;

